import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { AppSpinnerModule } from 'src/app/shared/modules/app-spinner.module';
import { FormError } from 'src/app/shared/components/app-form-error/form-error';
import { ListViewParticipant, ParticipantVerificationPatch } from 'src/app/shared/models/participant.model';
import { OrderDetailStore } from 'src/app/order-detail/+state/order-detail.store';
import { ParticipantsService } from 'src/app/shared/services/participants.service';
import { VerificationType } from 'src/app/shared/enums/verificationType';

@UntilDestroy()
@Component({
    selector: 'app-multifactor-code-form',
    templateUrl: './multifactor-code-form.component.html',
    styleUrls: ['./multifactor-code-form.component.scss'],
    standalone: false
})
export class MultifactorCodeFormComponent implements OnInit {
  multifactorCodeForm: UntypedFormGroup;
  participants$: Observable<ListViewParticipant[]>;

  @Input() verificationTypes: VerificationType[];
  @Input() selectedVerificationType: VerificationType;
  @Input() selectedParticipant: ListViewParticipant;

  @Output() onVerificationChange: EventEmitter<VerificationType> = new EventEmitter<VerificationType>();

  constructor(
    public store: OrderDetailStore,
    private modalService: NgbModal,
    private spinner: AppSpinnerModule,
    private toastr: ToastrService,
    private participantsService: ParticipantsService
  ) { }


  ngOnInit(): void {
    this.participants$ = this.store.participants$;
    this.participants$.pipe(
      filter(participants => participants !== undefined),
      tap(() => {
        this.initForm();
        this.patchForm();
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  initForm() {
    this.multifactorCodeForm = new UntypedFormGroup(
      {
        verificationType: new UntypedFormControl(VerificationType.MultiFactor, {
          validators: [
            this.validateByOptions(
              this.verificationTypes,
              "validVerificationType"
            ),
          ],
          updateOn: "change",
        }),
        phoneNumber: new UntypedFormControl(null, [
          Validators.required,
          Validators.pattern(/^[2-9]\d{9}$/),
        ]),
      },
    );
  }

  isFieldInvalidDirty(field: string): boolean {
    return this.multifactorCodeForm.get(field)?.invalid &&
      (this.multifactorCodeForm.get(field)?.touched ||
        this.multifactorCodeForm.get(field)?.dirty)
      ? true
      : false;
  }

  patchForm() {
    this.multifactorCodeForm
      .get("verificationType")
      ?.setValue(this.selectedVerificationType, {
        onlySelf: true,
      });
    this.multifactorCodeForm
      .get("phoneNumber")
      ?.setValue(this.selectedParticipant?.participantModifiable?.phoneNumber, {
        onlySelf: true,
      });
  }


  resetModal(reason: "cancel" | "saved") {
    this.modalService.dismissAll(reason);
    this.store.setIsEditing(false);
    this.spinner.hide();
  }

  onMulifactorCodeSuccess() {
    this.toastr.success('Participant saved successfully.');
    this.resetModal("saved");
  }

  onMulifactorCodeError(error: any) {
    this.toastr.error(error.message);
  }

  onDropdownChange(event: Event){
    const spanElement = event.target as HTMLSpanElement;
    const verificationType = spanElement?.innerText.trim() as VerificationType;

    this.onVerificationChange.emit(verificationType);
  }

  validateByOptions(options: VerificationType[], errorKey: string = "required") {
    return (control: AbstractControl): ValidationErrors | null =>
      options?.some((element: VerificationType) => element === control.value)
        ? null
        : { [errorKey]: { value: control.value } };
  }

  removeWhiteSpaceFromAllFields() {
    Object.keys(this.multifactorCodeForm.controls).forEach(key => {
      const currentValue = this.multifactorCodeForm.get(key)?.value;
      if (typeof currentValue === "string" && currentValue !== null && currentValue !== undefined) {
        const trimmedValue = currentValue.trim();
        this.multifactorCodeForm.get(key)?.setValue(trimmedValue);
      }
    });
  }

  filterToNumbers(value: string): string {
    if (!value) {
      return value;
    }

    return value.replace(/\D/g, "");
  }


  createPatchVerification(): ParticipantVerificationPatch{
    const formValue = this.multifactorCodeForm.getRawValue();

    let participantVerificationPatch: ParticipantVerificationPatch =  {
      phoneNumber:   this.filterToNumbers(formValue.phoneNumber) ?? "",
      verificationType: formValue.verificationType,
      pin: '',
    }

    return participantVerificationPatch;
  }


  patchVerification() {
    if (this.multifactorCodeForm.valid) {
      this.spinner.show();
      this.removeWhiteSpaceFromAllFields();
      let patchedVerification = this.createPatchVerification();
      this.participantsService
        .patchParticipantVerification(this.selectedParticipant.id, patchedVerification)
        .subscribe(
          (_) => {
            this.onMulifactorCodeSuccess();
            this.spinner.hide();
          },
          (error) => {
            this.onMulifactorCodeError(error);
            this.spinner.hide();
          }
        );
    } else {
      FormError.ValidateAllFormFields(this.multifactorCodeForm);
    }
  }
}
